<script>
import axios from "axios";

export default {
  name: "AdminStudentReferrals",

  data: () => ({
    isLoading: false,
    search: "",
    sortKey: "conversion", // conversion | applicants | contracts

    // The endpoint already returns stats per inviter, so this page holds one list, not two.
    rows: [],

    copiedId: null,
    exporting: false,

    page: 1,
    itemsPerPage: 20,

    revealed: false,
    anim: { students: 0, applicants: 0, applications: 0, contracts: 0 },
  }),

  computed: {
    filtered() {
      const q = (this.search || "").toString().toLowerCase().trim();
      if (!q) return this.rows;
      return this.rows.filter((m) =>
        (m.fullName || "").toLowerCase().includes(q)
      );
    },

    sorted() {
      const key = this.sortKey;
      const val = (m) =>
        key === "applicants"
          ? m.applicants
          : key === "contracts"
          ? m.reachedContract
          : m.conversion;
      return [...this.filtered].sort((a, b) => (val(b) || 0) - (val(a) || 0));
    },

    totals() {
      const sum = (k) => this.rows.reduce((a, x) => a + (Number(x[k]) || 0), 0);
      return {
        students: this.rows.length,
        applicants: sum("applicants"),
        applications: sum("applications"),
        contracts: sum("reachedContract"),
      };
    },

    paginationCount() {
      return Math.max(1, Math.ceil(this.sorted.length / this.itemsPerPage));
    },

    paged() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.sorted.slice(start, start + this.itemsPerPage);
    },
  },

  watch: {
    search() { this.page = 1; },
    sortKey() { this.page = 1; },
    paginationCount(n) { if (this.page > n) this.page = n; },
  },

  async mounted() {
    await this.refresh();
  },

  methods: {
    meterPct(n) {
      const base = this.totals.applicants || 0;
      return base > 0 ? Math.min(100, Math.round(((Number(n) || 0) / base) * 100)) : 0;
    },

    async refresh() {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const res = await axios.get("/admin/student-referral");
        this.rows = Array.isArray(res.data) ? res.data : [];
        this.$nextTick(() => {
          this.revealed = true;
          this.animateCounts();
        });
      } catch {
        this.rows = [];
      } finally {
        this.isLoading = false;
      }
    },

    animateCounts() {
      const targets = this.totals;
      const dur = 900;
      const start = Date.now();
      const from = { ...this.anim };
      const tick = () => {
        const t = Math.min(1, (Date.now() - start) / dur);
        const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
        this.anim = {
          students: Math.round(from.students + (targets.students - from.students) * e),
          applicants: Math.round(from.applicants + (targets.applicants - from.applicants) * e),
          applications: Math.round(from.applications + (targets.applications - from.applications) * e),
          contracts: Math.round(from.contracts + (targets.contracts - from.contracts) * e),
        };
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },

    referralLink(token) {
      return token ? `https://qabul.usat.uz/?student=${token}` : "";
    },

    async copyLink(item) {
      const link = this.referralLink(item.refToken);
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.copiedId = item.id;
      setTimeout(() => {
        if (this.copiedId === item.id) this.copiedId = null;
      }, 1400);
    },

    async exportExcel() {
      if (this.excelExportDisabled || this.exporting) return;
      this.exporting = true;
      try {
        const res = await axios.get("/admin/student-referral/export", {
          responseType: "blob",
        });
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `talaba-referallari-2026-2027-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // killswitch 403 / network error — button re-enables; nothing to render
      } finally {
        this.exporting = false;
      }
    },

    openStats(item) {
      this.$router.push(`/talaba-referallari-2026-2027/${item.id}`);
    },

    convPct(m) {
      return Math.max(0, Math.min(100, Number(m.conversion) || 0));
    },
    fmtPct(v) {
      const n = Number(v) || 0;
      return `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;
    },
    rank(m) {
      return this.sorted.indexOf(m) + 1;
    },
  },
};
</script>

<template>
  <v-container fluid class="rmx">
    <!-- TOP BAR — read-only panel: export only, no create -->
    <div class="rmx-topbar">
      <div class="rmx-actions">
        <v-btn
          class="modern-export"
          :loading="exporting"
          :disabled="exporting || excelExportDisabled"
          @click="exportExcel"
        >
          <img src="/icons/excel-icon.svg" alt="Excel" class="export-icon" />
          EXPORT
        </v-btn>
      </div>
    </div>

    <!-- COUNT CARDS -->
    <div class="royal-grid">
      <div class="royal-card royal--emp">
        <div class="royal-title">{{ $t('Talabalar') }}</div>
        <div class="royal-count">{{ anim.students }}</div>
        <div class="royal-meter"><span style="width: 100%"></span></div>
      </div>
      <div class="royal-card royal--apc">
        <div class="royal-title">{{ $t('Applicants') }}</div>
        <div class="royal-count">{{ anim.applicants }}</div>
        <div class="royal-meter"><span style="width: 100%"></span></div>
      </div>
      <div class="royal-card royal--app">
        <div class="royal-title">{{ $t('Applications') }}</div>
        <div class="royal-count">{{ anim.applications }}</div>
        <div class="royal-meter"><span :style="{ width: meterPct(totals.applications) + '%' }"></span></div>
      </div>
      <div class="royal-card royal--con">
        <div class="royal-title">{{ $t('Contracts') }}</div>
        <div class="royal-count">{{ anim.contracts }}</div>
        <div class="royal-meter"><span :style="{ width: meterPct(totals.contracts) + '%' }"></span></div>
      </div>
    </div>

    <!-- TOOLBAR (after counters, /applications style) -->
    <div class="toolbar-row">
      <v-text-field
        :placeholder="$t('Talabani izlash...')"
        append-icon="mdi-magnify"
        v-model="search"
        dense
        hide-details
        outlined
        clearable
        class="search-input"
      />
      <div class="circle-actions">
        <v-chip class="badge-count" color="primary" small>{{ sorted.length }}</v-chip>
        <div class="rmx-sort">
          <span class="rmx-sort-label">{{ $t('Rank by') }}</span>
          <button :class="{ on: sortKey === 'conversion' }" @click="sortKey = 'conversion'">{{ $t('Conversion') }}</button>
          <button :class="{ on: sortKey === 'applicants' }" @click="sortKey = 'applicants'">{{ $t('Applicants') }}</button>
          <button :class="{ on: sortKey === 'contracts' }" @click="sortKey = 'contracts'">{{ $t('Contracts') }}</button>
        </div>
      </div>
    </div>

    <!-- LEADERBOARD -->
    <div class="rmx-board">
      <div class="rmx-board-head">
        <span class="c-rank">#</span>
        <span class="c-name">{{ $t('Talaba') }}</span>
        <span class="c-link">{{ $t('Referral link') }}</span>
        <span class="c-num">{{ $t('Applicants') }}</span>
        <span class="c-num">{{ $t('Applications') }}</span>
        <span class="c-num">{{ $t('Contracts') }}</span>
        <span class="c-conv">{{ $t('Conversion') }}</span>
        <span class="c-act"></span>
      </div>

      <transition-group name="rmx-rise" tag="div">
        <div
          v-for="m in paged"
          :key="m.id"
          class="rmx-row"
          @click="openStats(m)"
        >
          <span class="c-rank">
            <span class="rmx-rk" :class="'rmx-rk--' + Math.min(rank(m), 4)">{{ rank(m) }}</span>
          </span>

          <span class="c-name">
            <span class="rmx-id">
              <b :title="m.fullName">{{ m.fullName }}</b>
              <em v-if="m.phone" class="rmx-sub">
                <span>{{ m.phone }}</span>
              </em>
            </span>
          </span>

          <span class="c-link" @click.stop>
            <code>?student={{ m.refToken }}</code>
            <button class="rmx-copy" :title="$t('Copy link')" @click="copyLink(m)">
              <v-icon size="14">{{ copiedId === m.id ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </button>
          </span>

          <span class="c-num">{{ m.applicants }}</span>
          <span class="c-num rmx-blue">{{ m.applications }}</span>
          <span class="c-num rmx-emerald">{{ m.reachedContract }}</span>

          <span class="c-conv">
            <span class="rmx-conv-pct">{{ fmtPct(m.conversion) }}</span>
            <span class="rmx-bar">
              <span class="rmx-bar-fill" :style="{ width: (revealed ? convPct(m) : 0) + '%' }"></span>
            </span>
          </span>

          <span class="c-act" @click.stop>
            <button class="rmx-icon" :title="$t('Period report')" @click="openStats(m)">
              <v-icon size="17">mdi-chart-timeline-variant</v-icon>
            </button>
          </span>
        </div>
      </transition-group>

      <div v-if="!sorted.length && !isLoading" class="rmx-empty">
        <div class="rmx-empty-mark">◎</div>
        <p>{{ $t('Hozircha hech kim taklif qilmagan') }}</p>
      </div>
    </div>

    <div class="rmx-pager" v-if="paginationCount > 1">
      <v-pagination v-model="page" :length="paginationCount" :total-visible="9" />
    </div>
  </v-container>
</template>

<style lang="scss">
@font-face {
  font-family: "Saira SemiCondensed";
  src: url("/SairaSemiCondensed/SairaSemiCondensed-Medium.woff2") format("woff2");
  font-weight: 500;
  font-display: swap;
}
@font-face {
  font-family: "Saira SemiCondensed";
  src: url("/SairaSemiCondensed/SairaSemiCondensed-SemiBold.woff2") format("woff2");
  font-weight: 600;
  font-display: swap;
}
@font-face {
  font-family: "Saira SemiCondensed";
  src: url("/SairaSemiCondensed/SairaSemiCondensed-Bold.woff2") format("woff2");
  font-weight: 700;
  font-display: swap;
}
@font-face {
  font-family: "Saira SemiCondensed";
  src: url("/SairaSemiCondensed/SairaSemiCondensed-Black.woff2") format("woff2");
  font-weight: 900;
  font-display: swap;
}
</style>

<style lang="scss" scoped>
/* ── Toolbar (search + actions) — /applications style ── */
.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0 12px;
}
.toolbar-row > .search-input { flex: 1; min-width: 0; }
.circle-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  max-width: 100%;
  flex-wrap: wrap;
  row-gap: 10px;
}
.circle-actions > * { flex-shrink: 0; }
.badge-count { font-size: 12px !important; padding: 4px 10px !important; font-weight: 600 !important; }
.badge-count.primary { color: white !important; }
.theme--dark .badge-count.primary { color: #111318 !important; }
::v-deep .search-input {
  .v-input__slot { background: #f1f5f9 !important; border-radius: 10px !important; min-height: 38px !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #d0d5dd !important; }
  &.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  .v-icon { color: #94a3b8 !important; font-size: 20px !important; }
  input { font-size: 14px; }
  input::placeholder { color: #94a3b8 !important; }
}
::v-deep .search-input.theme--dark {
  .v-input__slot { background: #22232e !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #33354a !important; }
  &.v-input--is-focused fieldset { border-color: #ffd451 !important; }
  .v-icon { color: #606275 !important; }
  input { color: #d5d6de !important; }
  input::placeholder { color: #606275 !important; }
}

/* Saralash segmented control — same as the Call markaz / Qabul xodimlar toolbars. Styles are
   scoped, so nothing carries over from those pages: without this the buttons render as bare
   run-together text and the unshrinkable block squeezes the search field down to its icon. */
.rmx-sort {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f5f5f5;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  padding: 4px;
  height: 44px;
}
.rmx-sort-label {
  align-self: stretch;
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rmx-muted);
  padding: 0 12px 0 6px;
  margin-right: 2px;
  border-right: 1px solid var(--rmx-line);
  white-space: nowrap;
}
.rmx-sort button {
  border: none;
  background: transparent;
  border-radius: 9px;
  padding: 7px 13px;
  font-size: 12.5px;
  font-weight: 700;
  color: #222;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.rmx-sort button:hover:not(.on) {
  color: #222;
  background: #e8e8e8;
}
.rmx-sort button.on {
  background: #ffffff;
  color: #222;
  font-weight: 800;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.14);
}
/* Dark mode: the light #f5f5f5/#222 above is for light mode → flip to dark bg + light text. */
.theme--dark .rmx-sort {
  background: #22232e;
  border-color: #33354a;
}
.theme--dark .rmx-sort button {
  color: #d5d6de;
}
.theme--dark .rmx-sort button:hover:not(.on) {
  color: #ffffff;
  background: #2a2c3a;
}
.theme--dark .rmx-sort button.on {
  background: #3a3c4e;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.rmx {
  --rmx-gold: #ffce4a;
  --rmx-gold-deep: #f4a915;
  --rmx-emerald: #34d399;
  --rmx-blue: #38bdf8;
  --rmx-bg: #f4f6fb;
  --rmx-panel: #ffffff;
  --rmx-ink: #0c1322;
  --rmx-text: #0f1a2e;
  --rmx-muted: #6b7891;
  --rmx-line: rgba(15, 26, 46, 0.09);
  --rmx-line-strong: rgba(15, 26, 46, 0.22);
  --rmx-tip-bg: #ffffff;
  --display: "Saira SemiCondensed", -apple-system, BlinkMacSystemFont, sans-serif;

  padding-bottom: 32px;
  font-feature-settings: "tnum" 1;
}

/* ───── TOP BAR ───── */
.rmx-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 2px 16px;
  flex-wrap: wrap;
}
.rmx-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
/* Excel button — same modern-export as the Qabul xodimlar page; kept shadowless to fit this flat page */
.modern-export {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 10px !important;
  background: #217346 !important;
  color: #fff !important;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  text-transform: none !important;
  transition: all 0.2s ease;
  height: 36px !important;
  min-width: 130px !important;
  box-shadow: none !important;
}
.modern-export:hover {
  background: #1a5c38 !important;
  box-shadow: none !important;
}
.modern-export:active {
  transform: scale(0.97);
}
.export-icon {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  margin-right: 10px;
}

/* ───── COUNT CARDS (royal — matches /contract_2026_2027) ───── */
.royal-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.royal-card {
  position: relative;
  overflow: hidden;
  background: var(--rmx-panel);
  border: 1px solid var(--rmx-line);
  border-radius: 14px;
  padding: 16px;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  transition: border-color 0.18s ease;
  animation: slideUpFade 0.38s ease both;
}
.royal-card:hover {
  border-color: var(--rmx-line-strong);
}
.royal-title {
  color: var(--rmx-muted);
  font-size: 12px;
  line-height: 1;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}
.royal-count {
  color: var(--rmx-text);
  font-family: var(--display);
  font-weight: 900;
  font-size: clamp(26px, 3.6vw, 38px);
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
}
.royal-meter {
  height: 6px;
  margin-top: 6px;
  border-radius: 999px;
  background: var(--rmx-line);
  overflow: hidden;
}
.royal-meter > span {
  display: block;
  height: 100%;
  width: 0;
  transition: width 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.royal--emp .royal-meter > span { background: var(--rmx-gold-deep); }
.royal--apc .royal-meter > span { background: #3451e6; }
.royal--app .royal-meter > span { background: #0ea5e9; }
.royal--con .royal-meter > span { background: var(--rmx-emerald); }
/* staggered entrance — matches /contract_2026_2027 */
.royal-card:nth-child(1) { animation-delay: 40ms; }
.royal-card:nth-child(2) { animation-delay: 80ms; }
.royal-card:nth-child(3) { animation-delay: 120ms; }
.royal-card:nth-child(4) { animation-delay: 160ms; }
@keyframes slideUpFade {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .royal-card { animation: none; }
}
@media (max-width: 1080px) {
  .royal-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 520px) {
  .royal-grid { grid-template-columns: 1fr; }
}

/* ───── PAGINATION ───── */
.rmx-pager {
  display: flex;
  justify-content: center;
  padding-top: 16px;
}
.rmx-pager ::v-deep .v-pagination__item,
.rmx-pager ::v-deep .v-pagination__navigation {
  box-shadow: none !important;
  border: 1px solid var(--rmx-line);
}
.rmx-pager ::v-deep .v-pagination__item--active {
  background: var(--rmx-ink) !important;
  color: var(--rmx-gold) !important;
  border-color: var(--rmx-ink) !important;
  box-shadow: none !important;
}


/* ───── BOARD ───── */
.rmx-board {
  background: var(--rmx-panel);
  border: 1px solid var(--rmx-line);
  border-radius: 16px;
  overflow: hidden;
}
.rmx-board-head,
.rmx-row {
  display: grid;
  grid-template-columns: 54px minmax(200px, 1.6fr) minmax(150px, 1.2fr) 96px 104px 92px 150px 60px;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
}
.rmx-board-head {
  height: 46px;
  border-bottom: 1px solid var(--rmx-line);
}
.rmx-board-head span {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: var(--rmx-muted);
}
.c-num,
.rmx-board-head .c-num {
  text-align: center;
}
.rmx-row {
  height: 66px;
  border-bottom: 1px solid var(--rmx-line);
  cursor: pointer;
  transition: background 0.16s ease, transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1),
    opacity 0.4s ease;
}
.rmx-row:hover {
  background: rgba(255, 206, 74, 0.06);
}
.rmx-row:last-child {
  border-bottom: none;
}
.c-num {
  font-family: var(--display);
  font-weight: 700;
  font-size: 18px;
  color: var(--rmx-text);
  font-variant-numeric: tabular-nums;
}
.c-num.rmx-blue { color: #0ea5e9; }
.c-num.rmx-emerald { color: #059669; }

.rmx-rk {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  font-weight: 800;
  font-size: 12px;
  color: var(--rmx-muted);
  background: rgba(15, 26, 46, 0.05);
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
.rmx-rk--1,
.rmx-rk--2,
.rmx-rk--3 {
  background: linear-gradient(135deg, #ffd451, #f5c542);
  color: #1a1500;
}

.c-name {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.rmx-id {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.rmx-id b {
  font-weight: 800;
  font-size: 14px;
  color: var(--rmx-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rmx-id em {
  font-style: normal;
  font-size: 11px;
  font-weight: 600;
  color: var(--rmx-muted);
  display: flex;
  align-items: center;
  gap: 6px;
}
.rmx-id .rmx-sub {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c-link {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.c-link code {
  font-size: 12px;
  color: var(--rmx-muted);
  background: rgba(15, 26, 46, 0.05);
  padding: 4px 8px;
  border-radius: 7px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}
.rmx-copy {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid var(--rmx-line);
  background: var(--rmx-panel);
  color: var(--rmx-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.15s ease;
}
.rmx-copy:hover {
  border-color: var(--rmx-gold-deep);
  color: var(--rmx-gold-deep);
}

.c-conv {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.rmx-conv-pct {
  font-family: var(--display);
  font-weight: 800;
  font-size: 16px;
  color: var(--rmx-text);
  font-variant-numeric: tabular-nums;
}
.rmx-bar {
  height: 6px;
  border-radius: 999px;
  background: rgba(15, 26, 46, 0.08);
  overflow: hidden;
}
.rmx-bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--rmx-gold), var(--rmx-emerald));
  transition: width 0.9s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.c-act {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}
.rmx-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid var(--rmx-line);
  background: var(--rmx-panel);
  color: var(--rmx-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: all 0.15s ease;
}
.rmx-icon:hover {
  border-color: var(--rmx-gold-deep);
  color: var(--rmx-gold-deep);
}

.rmx-empty {
  text-align: center;
  padding: 56px 20px;
}
.rmx-empty-mark {
  font-size: 46px;
  color: var(--rmx-line-strong);
  line-height: 1;
}
.rmx-empty p {
  margin: 14px 0 16px;
  color: var(--rmx-muted);
  font-weight: 600;
}

/* reveal — Telegram-style: fast, uniform, subtle fade, no per-row stagger. */
.rmx-rise-enter {
  opacity: 0;
  transform: translateY(6px);
}
.rmx-rise-enter-active {
  transition: opacity 0.14s ease-out, transform 0.14s ease-out;
}

@media (max-width: 1180px) {
  .rmx-board-head .c-link,
  .rmx-row .c-link {
    display: none;
  }
  .rmx-board-head,
  .rmx-row {
    grid-template-columns: 54px minmax(160px, 1.6fr) 84px 92px 84px 140px 60px;
  }
}
@media (max-width: 860px) {
  .rmx-board-head .c-num:nth-child(4),
  .rmx-row .c-num:nth-child(4),
  .rmx-board-head .c-num:nth-child(5),
  .rmx-row .c-num:nth-child(5) {
    display: none;
  }
  .rmx-board-head,
  .rmx-row {
    grid-template-columns: 46px minmax(140px, 1.6fr) 84px 130px 60px;
  }
}

/* ───── DARK ───── */
.theme--dark .rmx {
  --rmx-bg: #0c0e14;
  --rmx-panel: #161922;
  --rmx-ink: #090c14;
  --rmx-text: #eef1f7;
  --rmx-muted: #8b94a8;
  --rmx-line: rgba(255, 255, 255, 0.08);
  --rmx-line-strong: rgba(255, 255, 255, 0.2);
  --rmx-tip-bg: #1b1f2a;
}
.theme--dark .rmx-rk {
  background: rgba(255, 255, 255, 0.07);
}
/* Top-3 rank badges keep their gold in dark mode (otherwise dark text on a dark bg). */
.theme--dark .rmx-rk--1,
.theme--dark .rmx-rk--2,
.theme--dark .rmx-rk--3 {
  background: linear-gradient(135deg, #ffd451, #f5c542);
  color: #111318;
}
.theme--dark .c-num.rmx-blue { color: #7dd3fc; }
.theme--dark .c-num.rmx-emerald { color: #6ee7b7; }
.theme--dark .c-link code,
.theme--dark .rmx-bar {
  background: rgba(255, 255, 255, 0.07);
}
@media (max-width: 600px) {
  .rmx-board { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .rmx-board-head, .rmx-row { min-width: 600px; }
}
</style>
